// === OPPDATERT MaintenanceWarning.tsx ===
// Erstatt hele MaintenanceWarning.tsx med denne:

import React, { useState, useEffect, useRef } from 'react';
import { AlertTriangle, X, Clock } from 'lucide-react';

interface MaintenanceWarningProps {
  isVisible: boolean;
  message: string;
  minutesUntil: number;
  onDismiss: () => void;
}

const MaintenanceWarning: React.FC<MaintenanceWarningProps> = ({
  isVisible,
  message,
  minutesUntil,
  onDismiss
}) => {
  // Bruk useRef for å holde styr på timeren
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [isDismissed, setIsDismissed] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // Initialiser timer når komponenten vises eller minutesUntil endres
  useEffect(() => {
    if (isVisible && !isDismissed && minutesUntil > 0) {
      console.log(`Starter nedtelling: ${minutesUntil} minutter`);
      setTimeRemaining(minutesUntil * 60);
      setIsMinimized(false);
      
      // Clear eksisterende interval
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      
      // Start ny interval
      intervalRef.current = setInterval(() => {
        setTimeRemaining((prev) => {
          if (prev <= 1) {
            if (intervalRef.current) {
              clearInterval(intervalRef.current);
              intervalRef.current = null;
            }
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isVisible, minutesUntil, isDismissed]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleDismiss = () => {
    setIsDismissed(true);
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    onDismiss();
  };

  const handleMinimize = () => {
    setIsMinimized(!isMinimized);
  };

  // Ikke vis hvis ikke synlig, avvist eller tiden er ute
  if (!isVisible || isDismissed || timeRemaining <= 0) {
    return null;
  }

  // Minimert visning
  if (isMinimized) {
    return (
      <div 
        className="fixed top-4 right-4 z-50 bg-orange-500 text-white rounded-lg shadow-lg p-3 cursor-pointer animate-pulse hover:bg-orange-600 transition-colors"
        onClick={handleMinimize}
      >
        <div className="flex items-center space-x-2">
          <AlertTriangle className="h-5 w-5" />
          <span className="font-bold tabular-nums">{formatTime(timeRemaining)}</span>
        </div>
      </div>
    );
  }

  // Full visning
  return (
    <div className="fixed top-0 left-0 right-0 z-50 animate-slide-down">
      <div className="bg-gradient-to-r from-yellow-500 to-orange-500 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3 flex-1">
              <div className="flex-shrink-0">
                <AlertTriangle className="h-6 w-6 text-white animate-pulse" />
              </div>
              <div className="flex-1">
                <p className="text-white font-semibold">
                  Planlagt vedlikehold starter snart
                </p>
                <p className="text-yellow-100 text-sm">
                  {message || 'Systemet vil være utilgjengelig under vedlikeholdet'}
                </p>
              </div>
              <div className="flex items-center space-x-2 bg-white bg-opacity-20 rounded-lg px-3 py-2">
                <Clock className="h-5 w-5 text-white" />
                <span className="text-white font-bold text-lg tabular-nums">
                  {formatTime(timeRemaining)}
                </span>
              </div>
            </div>
            <div className="flex items-center space-x-2 ml-4">
              <button
                onClick={handleMinimize}
                className="p-1 rounded-lg hover:bg-white hover:bg-opacity-20 transition-colors"
                title="Minimer varsel"
              >
                <svg className="h-5 w-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              <button
                onClick={handleDismiss}
                className="p-1 rounded-lg hover:bg-white hover:bg-opacity-20 transition-colors"
                title="Skjul varsel permanent"
              >
                <X className="h-5 w-5 text-white" />
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="h-1 bg-yellow-600 bg-opacity-30">
        <div
          className="h-full bg-yellow-600 transition-all duration-1000 ease-linear"
          style={{
            width: `${(timeRemaining / (minutesUntil * 60)) * 100}%`,
          }}
        />
      </div>
    </div>
  );
};

export default MaintenanceWarning;